/**
 * CommandPickerParts — the rows `CommandPickerPopover` renders inside its cmdk
 * list: an item (check column, label, badges, muted meta), the "Use <typed
 * text>" custom row, a group heading with its bulk action, and the footer
 * scope toggle. Split out so the popover file keeps to its state and layout.
 */

import { Check, PenLine } from 'lucide-react';

import { CommandItem } from '../../../../../components/ui/command';
import type { CommandPickerGroupAction, CommandPickerItem, CommandPickerScopeToggle } from './CommandPickerPopover';
import { VqeTooltip } from './VqeTooltip';

export function PickerItemRow({
  testId,
  item,
  onSelect,
}: {
  readonly testId: string;
  readonly item: CommandPickerItem;
  readonly onSelect: (key: string) => void;
}) {
  return (
    <CommandItem
      value={item.value ?? (typeof item.label === 'string' ? `${item.label} ${item.key}` : item.key)}
      data-testid={`${testId}-item-${item.key}`}
      data-checked={item.checked === undefined ? undefined : String(item.checked)}
      onSelect={() => onSelect(item.key)}
      disabled={item.disabled}
      className="gap-2"
    >
      {item.checked === undefined ? null : (
        <Check aria-hidden className={`h-3 w-3 shrink-0 text-primary ${item.checked ? '' : 'invisible'}`} />
      )}
      <span className="flex-1 truncate font-mono">{item.label}</span>
      {item.badges}
      {item.meta ? (
        <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">{item.meta}</span>
      ) : null}
    </CommandItem>
  );
}

/** The "Use <typed text>" row — force-mounted, so cmdk's filter never hides it. */
export function CustomEntryRow({
  testId,
  text,
  meta,
  onPick,
}: {
  readonly testId: string;
  readonly text: string;
  readonly meta?: string;
  readonly onPick: () => void;
}) {
  return (
    <CommandItem
      forceMount
      value={`custom-entry:${text}`}
      data-testid={`${testId}-custom`}
      onSelect={onPick}
      className="mx-1 mb-1 gap-2"
    >
      <PenLine aria-hidden className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate">
        Use <span className="font-mono font-semibold">{text}</span>
      </span>
      <span className="shrink-0 text-[10px] text-muted-foreground">{meta}</span>
    </CommandItem>
  );
}

export function GroupHeadingWithAction({
  testId,
  heading,
  action,
  onGroupAction,
}: {
  readonly testId: string;
  readonly heading?: string;
  readonly action: CommandPickerGroupAction;
  readonly onGroupAction: (key: string) => void;
}) {
  return (
    <span className="flex items-center justify-between gap-2">
      <span className="truncate">{heading}</span>
      <VqeTooltip content={action.tooltip}>
        <button
          type="button"
          // Heading lives outside cmdk's item navigation; stop the mousedown so
          // the popover's focus-outside guard doesn't fire mid-click.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onGroupAction(action.key)}
          data-testid={`${testId}-group-action-${action.key}`}
          className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium text-primary transition-transform hover:bg-primary/10 active:scale-[0.96]"
        >
          {action.label}
        </button>
      </VqeTooltip>
    </span>
  );
}

export function ScopeToggleRow({
  testId,
  toggle,
}: {
  readonly testId: string;
  readonly toggle: CommandPickerScopeToggle;
}) {
  return (
    <div
      className="flex items-center justify-between border-t border-border px-2 py-1.5 text-[11px] text-muted-foreground"
      data-testid={`${testId}-scope-toggle`}
    >
      <span>{toggle.showingAll ? 'Showing all tables' : 'Showing tables from query'}</span>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => toggle.onToggle()}
        data-testid={`${testId}-scope-toggle-btn`}
        className="rounded px-1.5 py-0.5 font-medium text-primary transition-transform hover:bg-primary/10 active:scale-[0.96]"
      >
        {toggle.showingAll ? 'Show only query tables' : 'Show all tables'}
      </button>
    </div>
  );
}
